import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AREA_OF_FRAUD_LABEL_MAP, Case } from "../../../api/cases";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import CaseStatusBadge from "../../../components/ui/casestatusbadge/CaseStatusBadge";
import SimpleTable, { TableRow } from "../../../components/ui/simpletable/SimpleTable";
import { formatDateForDisplay, toTitleCase } from "../../../lib/utils";
import { getCaseDealingOfficerIds, getCaseListStatus, getCases } from "../logic/CaseListSlice";
import { getPageNumber, getPageSize, setPageNumber, setPageSize } from "../logic/CaseFiltersSlice";
import { fetchCases } from "../logic/Thunks";
import { getInvestigators, getUsersByIds } from "../../users/logic/slice";
import { fetchUsersWithUserIds } from "../../users/logic/thunk";
import { CaseEvent, EventType } from "../../../api/events";
import { ParticipantType, Participation } from "../../../../src/api/participants";
import { isCreditAccountStakeholdersRevampEnabled } from "../../configs/logic/Slice";
import { getAccountNames, getKeyPersonnel } from "../utils/caseListColumns";



const CaseListTable: React.FC = () => {

    const cases = useAppSelector(getCases);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const investigators = useAppSelector(getInvestigators);
    const isCreditRevampEnabled = useAppSelector(isCreditAccountStakeholdersRevampEnabled);

    const getInvestigatorName = (participations?: Participation[]): string => {
        if (!participations) return "-";

        const investigatorId = participations.find(
        (p) => p.participant_type === ParticipantType.INVESTIGATOR
        )?.participant_id;

        if (!investigatorId) return "-";

        const investigator = investigators.find(
        (user) => user.user_id === investigatorId
        );

        return investigator?.user_name || "-";
    };

    const dealingOfficerIds = useAppSelector(getCaseDealingOfficerIds);

    const dealingOfficersById = useAppSelector((state) =>
        getUsersByIds(state, dealingOfficerIds)
    );

    useEffect(() => {
        if (dealingOfficerIds.length) {
            dispatch(fetchUsersWithUserIds(dealingOfficerIds));
        }
    }, [dispatch, dealingOfficerIds]);

    const getDealingOfficerName = (participations?: Participation[]): string => {
        if (!participations) return "-";

        const dealingOfficerId = participations.find(
        (p) => p.participant_type === ParticipantType.CASE_DEALING_OFFICER
        )?.participant_id;

        if (!dealingOfficerId) return "-";

        return dealingOfficersById[dealingOfficerId]?.user_name || "-";
    };

    const header_row: TableRow = {
        cells: [
            {
                content: "UIN"
            },
            {
                content: "STATUS"
            },
            {
                content: "AREA OF FRAUD"
            },
            ...(isCreditRevampEnabled
                ? [{ content: "ACCOUNT NAME" }, { content: "KEY PERSONNEL" }]
                : [{ content: "PARTIES INVOLVED" }]
            ),
            {
                content: "REGION"
            },
            {
                content: "BRANCH"
            },
            {
                content: "PRODUCT"
            },
            {
                content: "INVESTIGATOR"
            },
            {
                content: "DEALING OFFICER"
            },
            {
                content: "DATE REFERRED"
            },
            {
                content: "DATE OF OCCURRENCE"
            },
            {
                content: "DATE OF DETECTION"
            },
            {
                content: "DATE OF CLASSIFICATION"
            },
            {
                content: "DATE OF REPORTING TO RBI"
            }
        ]
    }

    const render_truncated_list = (values: string[]) => {
        const full_text = values.join(", ");
        const display_text = full_text.length > 40 ? full_text.slice(0, 37) + "..." : full_text
        return <span title={full_text}>{display_text}</span>
    }

    const render_truncated_list_or_dash = (values: string[]) => (
        values.length ? render_truncated_list(values) : "-"
    )

    const render_event_date = (eventType: EventType, events?: CaseEvent[]): string => {
        if (!events) return "-";

        const event = events.find(e => e.event_type === eventType);
        return event ? formatDateForDisplay(new Date(event.date)) : "-";
    }

    const map_cases_to_rows = (cases: Case[]): TableRow[] => (
        cases.map(case_data => ({
            cells: [
                {
                    content: <span className="font-medium text-[#0A0A0A]">{case_data.uin}</span>
                },
                {
                    content: case_data.status ? <CaseStatusBadge status={case_data.status}/> : "-"
                },
                {
                    content: case_data.area_of_fraud ? AREA_OF_FRAUD_LABEL_MAP[case_data.area_of_fraud] : "-"
                },
                ...(isCreditRevampEnabled
                    ? [
                        {
                            content: render_truncated_list_or_dash(getAccountNames(case_data))
                        },
                        {
                            content: render_truncated_list_or_dash(getKeyPersonnel(case_data))
                        }
                    ]
                    : [
                        {
                            content: render_truncated_list((case_data.parties_involved || []).map(party => party.name + (party.party_type ? "(" + toTitleCase(party.party_type) + ")": "")))
                        }
                    ]
                ),
                {
                    content: case_data.region
                },
                {
                    content: case_data.branch
                },
                {
                    content: render_truncated_list(Array.from(new Set(case_data.loan_product_info?.map(loan => loan.product).filter(Boolean) ?? [])))
                },
                {
                    content: getInvestigatorName(case_data.participations)
                },
                {
                    content: getDealingOfficerName(case_data.participations)
                },
                {
                    content: formatDateForDisplay(new Date(case_data.referral_date))
                },
                {
                    content: render_event_date(EventType.DATE_OF_OCCURENCE, case_data.events)
                },
                {
                    content: render_event_date(EventType.DATE_OF_DETECTION, case_data.events)
                },
                {
                    content: render_event_date(EventType.DATE_OF_CLASSIFICATION_CA_APPROVAL, case_data.events)
                },
                {
                    content: render_event_date(EventType.FMR1_FILED, case_data.events)
                }
            ],
            onClick: () => {navigate(`/cases/${case_data.case_id}`)}
        }))
    )

    const data_rows = map_cases_to_rows(cases);

    const page_number = useAppSelector(getPageNumber)
    const page_size = useAppSelector(getPageSize)
    const status = useAppSelector(getCaseListStatus)

    const on_page_change = (page_number: number) => {
        dispatch(setPageNumber(page_number))
        dispatch(fetchCases())
    }

    const on_page_size_change = (page_size: number) => {
        dispatch(setPageSize(page_size));
        dispatch(fetchCases());
    }
    
    const onRetry = () => {
        dispatch(fetchCases())
    }

    return (
        <>
            <SimpleTable 
                header_row={header_row} 
                data_rows={data_rows} 
                empty_table_message="No cases found matching your filters."
                pagination={{
                    page_number: page_number,
                    page_size: page_size,
                    on_page_change: on_page_change,
                    on_page_size_change: on_page_size_change,
                }}
                loading={status === 'loading'}
                error={status === 'error'}
                onRetry={onRetry}
            />
        </>
    );
};

export default CaseListTable;